import { keepPreviousData, queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { TimeLineDataSet } from '@/common/api/types/planner/timelineItem';
import { isHttpError } from '@/common/utils/isHttpError';
import type { ScenarioItemFilters } from '@/features/planner/api/adaptive-planner/scenarioItemFilters';
import { fetchReportingStorePlannerDataSet } from '@/features/planner/api/plannerDataSet';
import { plannerQueryKeys } from '@/features/planner/queryKeys';

interface TimelineItemsDataSetParams {
  loopId: number;
  scenarioId: number;
  filters?: ScenarioItemFilters;
  order?: string;
}

export type DataSetRefetchInterval = (query: {
  state: { data?: TimeLineDataSet[] };
}) => number | false;

interface TimelineItemsDataSetHookParams extends TimelineItemsDataSetParams {
  refetchInterval?: DataSetRefetchInterval;
}

const REPORTING_STORE_MIRROR_LAG_RETRY_LIMIT = 15;
const REPORTING_STORE_MIRROR_LAG_RETRY_DELAY_MS = 3000;
const DEFAULT_RETRY_LIMIT = 3;
const DEFAULT_RETRY_DELAY_MS = 30000;

export const getTimelineItemsDataSetQueryOptions = ({
  loopId,
  scenarioId,
  filters,
  order,
}: TimelineItemsDataSetParams) =>
  queryOptions({
    queryKey: plannerQueryKeys.timelineItems.dataSet({ loopId, scenarioId, filters, order }),
    queryFn: () => fetchReportingStorePlannerDataSet({ loopId, scenarioId, filters, order }),
    retry: (failureCount: number, error: unknown) =>
      isHttpError(error, 404)
        ? failureCount < REPORTING_STORE_MIRROR_LAG_RETRY_LIMIT
        : failureCount < DEFAULT_RETRY_LIMIT,
    retryDelay: (attempt: number, error: unknown) =>
      isHttpError(error, 404)
        ? Math.min(1000 * 2 ** attempt, REPORTING_STORE_MIRROR_LAG_RETRY_DELAY_MS)
        : Math.min(1000 * 2 ** attempt, DEFAULT_RETRY_DELAY_MS),
  });

export const useGetTimelineItemsDataSetSuspenseQuery = ({
  loopId,
  scenarioId,
  filters,
  order,
  refetchInterval,
}: TimelineItemsDataSetHookParams) => {
  return useSuspenseQuery({
    ...getTimelineItemsDataSetQueryOptions({ loopId, scenarioId, filters, order }),
    refetchInterval,
  });
};

export const useGetTimelineItemsDataSetQuery = ({
  loopId,
  scenarioId,
  filters,
  order,
  refetchInterval,
}: TimelineItemsDataSetHookParams) => {
  return useQuery({
    ...getTimelineItemsDataSetQueryOptions({ loopId, scenarioId, filters, order }),
    placeholderData: keepPreviousData,
    refetchInterval,
  });
};
